<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
// Vue
import { computed, ref, onMounted, watch } from "vue"
import { useI18n } from "vue-i18n"

// Helpers
import { formatDate } from "@core/helpers"

// Types
import { Note } from "@types"

// Store
import { useSourceStore } from "@core/stores/source.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
const sourceStore = useSourceStore()
const buildingStore = useBuildingStore()

import { storeToRefs } from "pinia"
const { isFetchingSourceNotes } = storeToRefs(sourceStore)
const { getBuildingIsViewOnly } = storeToRefs(buildingStore)

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            info_note: ({ named }: { named: (key: string) => string }) => {
                return `${named("createdAt") !== named("updatedAt") ? "Modifié" : "Ajoutée"} le ${formatDate(named("updatedAt"))} par ${named("firstname")} ${named("lastname")}`
            },
            button: {
                add_note: "Nouvelle note",
            },
            no_notes: "Aucune note disponible.",
            error: {
                company_source:
                    "Vous n'avez pas les droits pour voir ou ajouter des notes",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    meterId: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// REACTIVE STATE
// ================================

// variable pour gérer l'ouvreture ou la fermeture de la modale d'édition
const modalEditNoteToggle = ref<boolean>(false)
// variable pour gérer l'ouvreture ou la fermeture de la modale de suppression
const modalDeleteNoteToggle = ref<boolean>(false)
// variable pour stocket l'id de la note à modifier ou supprimer
const noteToChange = ref<Note | null>(null)
const errorNoCompanySource = ref<boolean>(false)
// ================================
// WATCHERS
// ================================

// watcher quand la modal d'édition est fermé on réinitialise noteToChange
watch(
    () => modalEditNoteToggle.value,
    (newVal) => {
        if (newVal === false) {
            noteToChange.value = null
        }
    }
)
// watcher quand la modal de suppression est fermé on réinitialise noteToChange
watch(
    () => modalDeleteNoteToggle.value,
    (newVal) => {
        if (newVal === false) {
            noteToChange.value = null
        }
    },
    { immediate: true }
)

// ================================
// LIFCYCLE
// ================================
onMounted(async () => {
    fetchNotes()
})

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Computed pour récupérer les infos de la source
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})

// ================================
// METHODS
// ================================

/**
 * méthode pour récupérer les notes
 */
const fetchNotes = async () => {
    // on appelle le store pour récupérer le notes à jour
    await sourceStore.fetchSourceNotes(props.meterId)
    if (
        sourceStore?.hasErrorSourceNotes &&
        sourceStore?.errorSourceNotes?.code === "not_found_companysource"
    ) {
        errorNoCompanySource.value = true
    } else errorNoCompanySource.value = false
}

/**
 * méthode pour ouvrir la modal d'édition
 * @param note: id de la note ou null pour une nouvelle note
 */
function onOpenModalEditNote(note: Note | null): void {
    // on définit noteToChange avec l'id de la note à changer
    noteToChange.value = note
    // on ouvre la modal d'édition
    modalEditNoteToggle.value = true
}

/**
 * méthode pour ouvrir la modal de suppression
 * @param note: id de la note
 */
function onDeleteNote(note: Note): void {
    // on définit noteToChange avec l'id de la note à supprimer
    noteToChange.value = note
    // on ouvre la modal de suppression
    modalDeleteNoteToggle.value = true
}
</script>

<template>
    <div class="meter-card-notes">
        <!-- Si la note est toujours en cours d'éxécution on affiche des loaders -->
        <div v-if="isFetchingSourceNotes" class="mt-4">
            <BaseSkeleton width="100%" height="10vh"> </BaseSkeleton>
        </div>
        <div v-if="errorNoCompanySource">
            <BaseMessage severity="warn" class="mt-6">
                {{ t("error.company_source") }}
            </BaseMessage>
        </div>
        <div
            v-if="!isFetchingSourceNotes && !errorNoCompanySource"
            class="mt-4"
        >
            <!-- Si il y à des notes on les affiches -->
            <ul
                v-if="getSourceById?.notes && getSourceById?.notes.length > 0"
                class="flex flex-col gap-4"
            >
                <li
                    v-for="(note, index) in getSourceById?.notes"
                    :key="index"
                    class="p-4 border border-gray-200 rounded-lg flex flex-col gap-4"
                >
                    <div class="flex flex-row justify-between">
                        <!-- texte de la note -->
                        <p>{{ note.text }}</p>
                        <div v-if="!getBuildingIsViewOnly">
                            <!-- boutton pour supprimer la note -->
                            <BaseButton
                                icon="pi pi-trash"
                                variant="text"
                                rounded
                                aria-label="Filter"
                                @click="onDeleteNote(note)"
                            />
                            <!-- boutton pour éditer la note -->
                            <BaseButton
                                icon="pi pi-pencil"
                                variant="text"
                                rounded
                                aria-label="Filter"
                                @click="onOpenModalEditNote(note)"
                            />
                        </div>
                    </div>
                    <p>
                        {{
                            t("info_note", {
                                createdAt: note.createdAt,
                                updatedAt: note.updatedAt,
                                firstname: note.updated_by.firstname,
                                lastname: note.updated_by.lastname,
                            })
                        }}
                    </p>
                </li>
            </ul>
            <!-- Si il n'y à aucunes notes on affiche un texte -->
            <p v-else class="text-gray-500">{{ t("no_notes") }}</p>
        </div>
        <div
            v-if="!errorNoCompanySource && !getBuildingIsViewOnly"
            class="text-center"
        >
            <!-- boutton pour ajouter une nouvelle note: ouvre la modal d'édition -->
            <BaseButton
                class="max-w-fit mx-auto mt-6"
                :label="t('button.add_note')"
                variant="outlined"
                severity="contrast"
                icon="pi pi-plus"
                @click="onOpenModalEditNote(null)"
            ></BaseButton>
        </div>
        <!-- Modal de modification/ajout de notes -->
        <DialogMeterEditNote
            v-model:visible="modalEditNoteToggle"
            :meter-id="meterId"
            :note-id="noteToChange ? noteToChange?._id : null"
            :note="noteToChange ? noteToChange?.text : null"
            @update="fetchNotes"
        />
        <!-- Dialog suppression de note -->
        <DialogMeterDeleteNote
            v-model:visible="modalDeleteNoteToggle"
            :meter-id="meterId"
            :note-id="noteToChange?._id"
            @update="fetchNotes"
        />
    </div>
</template>

<style scoped></style>
